import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { setNoIndex } from "@/utils/seo";
import axios from "axios";
import { LogIn, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { LOGO_URL } from "@/constants/categories";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [step, setStep] = useState("password");
  const [ticket, setTicket] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Area riservata | HairHelp";
    setNoIndex();
  }, []);

  const submitPassword = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      if (data.twofa_required) {
        setTicket(data.ticket);
        setStep("2fa");
      } else {
        setUser(data);
        navigate("/admin/dashboard");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/2fa/verify`, { ticket, code: code.trim() }, { withCredentials: true });
      setUser(data);
      navigate("/admin/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <div className="w-full max-w-md rounded-3xl bg-card p-10 shadow-soft" data-testid="login-card">
        <Link to="/" className="flex justify-center" data-testid="login-logo-link">
          <img src={LOGO_URL} alt="HairHelp" className="h-14 w-auto" />
        </Link>

        {step === "password" ? (
          <>
            <h1 className="mt-8 text-center font-heading text-xl font-semibold text-foreground" data-testid="login-title">
              Area riservata
            </h1>
            <p className="mt-2 text-center text-sm text-muted-foreground">Accedi alla dashboard di gestione</p>
            <form onSubmit={submitPassword} className="mt-8 space-y-5" data-testid="login-form">
              <div className="space-y-2">
                <Label htmlFor="admin-email">Email</Label>
                <Input
                  id="admin-email"
                  type="email"
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="focus-visible:ring-primary"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-password">Password</Label>
                <Input
                  id="admin-password"
                  type="password"
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="focus-visible:ring-primary"
                />
              </div>
              {error && (
                <p className="rounded-xl bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive" data-testid="login-error">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={loading}
                data-testid="login-submit-button"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-7 py-3.5 text-base font-semibold text-primary-foreground shadow-soft transition-transform duration-300 ease-out hover:scale-[1.02] active:scale-95 disabled:opacity-60"
              >
                <LogIn className="h-4 w-4" />
                {loading ? "Accesso…" : "Accedi"}
              </button>
            </form>
          </>
        ) : (
          <>
            <h1 className="mt-8 text-center font-heading text-xl font-semibold text-foreground" data-testid="login-2fa-title">
              Verifica in due passaggi
            </h1>
            <p className="mt-2 text-center text-sm text-muted-foreground">
              Inserisci il codice a 6 cifre della tua app di autenticazione, oppure un codice di recupero.
            </p>
            <form onSubmit={submitCode} className="mt-8 space-y-5" data-testid="login-2fa-form">
              <div className="space-y-2">
                <Label htmlFor="admin-2fa">Codice</Label>
                <Input
                  id="admin-2fa"
                  type="text"
                  inputMode="text"
                  data-testid="login-2fa-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  autoComplete="one-time-code"
                  autoFocus
                  className="text-center tracking-[0.3em] focus-visible:ring-primary"
                />
              </div>
              {error && (
                <p className="rounded-xl bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive" data-testid="login-2fa-error">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={loading}
                data-testid="login-2fa-submit"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-7 py-3.5 text-base font-semibold text-primary-foreground shadow-soft transition-transform duration-300 ease-out hover:scale-[1.02] active:scale-95 disabled:opacity-60"
              >
                <ShieldCheck className="h-4 w-4" />
                {loading ? "Verifica…" : "Verifica"}
              </button>
              <button
                type="button"
                onClick={() => { setStep("password"); setCode(""); setError(""); }}
                data-testid="login-2fa-back"
                className="w-full text-center text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                Torna indietro
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
